import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";

import BlockAccordion from "@components/accordions/BlockAccordion";
import styles from "@components/ResourceFeatures.module.css";
import BodyCopy from "@components/typography/BodyCopy";
import TextareaText from "@components/typography/TextareaText";
import useMyExpert from "@hooks/useMyExpert";
import { ResourceFeatureKindEnum, ResourceFragment } from "@typing/Generated";
import { sortByPosition } from "@utils/modelUtils";

type ResourceType = ResourceFragment;

type Props = {
  resource: ResourceType;
};

const ResourceFeatures = ({ resource }: Props) => {
  const myExpert = useMyExpert();

  const orderedFeatures = useMemo(() => {
    const orderedFiltered = sortByPosition(resource.task.resourceFeatures).filter(
      feature => myExpert || resource.featureValues.some(fv => fv.resourceFeature.id === feature.id)
    );
    return orderedFiltered.map(feature => ({
      featureValue: resource.featureValues.find(fv => fv.resourceFeature.id === feature.id),
      resourceFeature: feature
    }));
  }, [myExpert, resource.featureValues, resource.task.resourceFeatures]);

  if ((myExpert && orderedFeatures.length > 0) || resource.featureValues.length > 0) {
    return (
      <div className="ion-margin-bottom">
        <BlockAccordion
          color="white"
          content={
            <IonGrid className={`ion-no-padding ion-no-margin`}>
              <IonRow className="ion-no-padding ion-no-margin">
                {orderedFeatures.map(featureData => (
                  <IonCol className={styles.featureValue} key={featureData.resourceFeature.id} size="6">
                    <div className={styles.featureName}>
                      <BodyCopy bold>{featureData.resourceFeature.name}</BodyCopy>
                    </div>
                    <div>
                      <BodyCopy>
                        {featureData.resourceFeature.kind === ResourceFeatureKindEnum.BOOLEAN ? (
                          <>
                            {typeof featureData.featureValue?.booleanValue === "boolean" && (
                              <FormattedMessage
                                id={`dictionary.${featureData.featureValue.booleanValue ? "yes" : "no"}`}
                              />
                            )}
                          </>
                        ) : (
                          <TextareaText text={featureData.featureValue?.textValue} />
                        )}
                      </BodyCopy>
                    </div>
                  </IonCol>
                ))}
              </IonRow>
            </IonGrid>
          }
          heading={<FormattedMessage id="pages.carePlan.task.resources.viewFeatures" />}
        />
      </div>
    );
  }

  return null;
};

export default ResourceFeatures;
